import { toast } from 'react-toastify'
import { IQuestion, IRuleAction, IRuleSet } from '../api/types/checklist'
import IResult, { ResultAction } from '../api/types/result'
import { isExpectedDeviation } from '../util/deviation'

// same as the backend (ResultsHelper#parse_rule_number): "7,5", "7.5", "-2", "1.234,5" and "1 500" as numbers,
// null for anything else ("n/a", "", "abc"). A single dot is a decimal point, commas are when present.
export function parseRuleNumber(text: string | null | undefined): number | null {
  let clean = `${text ?? ''}`.trim().replace(/ /g, '')
  if (clean.includes(',')) clean = clean.replace(/\./g, '').replace(/,/g, '.')
  if ((clean.match(/\./g) || []).length > 1) clean = clean.replace(/\./g, '')
  return /^-?\d+(\.\d+)?$/.test(clean) ? parseFloat(clean) : null
}

// multi select: the selection must be exactly the expected items (the backend accepts any of them;
// kept as it was for the offline companies, roadmap 09-rule-engine)
const equalsIgnoreOrder = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false
  const uniqueValues = new Set([...a, ...b])
  for (const v of uniqueValues) {
    if (a.filter((e) => e === v).length !== b.filter((e) => e === v).length) return false
  }
  return true
}

export default class RuleEngine {
  public static checkRules(result: IResult, results: IResult[]): IResult {
    if (result.question && result.question.rule_sets) {
      const actions = result.question.rule_sets.map((x) => RuleEngine.processRuleSet(
        x,
        result.question,
        result.value,
        results
      ))
      result.action = actions.flat()
    }
    return result
  }

  private static processRuleSet(
    ruleSet: IRuleSet,
    question: IQuestion,
    value: string,
    results: IResult[]
  ): ResultAction[] {
    const actions: ResultAction[] = []
    const checkOk = RuleEngine.compareRules(question, ruleSet, value)
    const action = ruleSet.rule_action
    if (action) {
      console.log(`Processing action of type ${action.action_type}`);
      switch (action.action_type) {
        case 'show_question':
          actions.push(checkOk
            ? {
              action: 'show_question',
              target_question_ids: action.target_question_ids
            }
            : {
              action: 'hide_question',
              target_question_ids: action.target_question_ids
            })
          break
        case 'hide_question':
          actions.push(checkOk
            ? {
              action: 'hide_question',
              target_question_ids: action.target_question_ids
            }
            : {
              action: 'show_question',
              target_question_ids: action.target_question_ids
            })
          break
        case 'show_area':
          actions.push(checkOk
            ? {
              action: 'show_area',
              target_area_ids: action.target_area_ids,
              target_area_count: action.target_area_count
            }
            : {
              action: 'hide_area',
              target_area_ids: action.target_area_ids,
              target_area_count: action.target_area_count
            })
          break
        case 'hide_area':
          actions.push(checkOk
            ? {
              action: 'hide_area',
              target_area_ids: action.target_area_ids,
              target_area_count: action.target_area_count
            }
            : {
              action: 'show_area',
              target_area_ids: action.target_area_ids,
              target_area_count: action.target_area_count
            })
          break
        case 'duplicate_area': {
          actions.push(checkOk
            ? {
              action: 'duplicate_area',
              target_area_ids: action.target_area_ids,
              target_area_count: RuleEngine.targetCount(action, question, value, results)
            }
            : {
              action: 'duplicate_area',
              target_area_ids: action.target_area_ids,
              target_area_count: 1
            })
          break
        }
        case 'duplicate_question': {
          actions.push(checkOk
            ? {
              action: 'duplicate_question',
              target_question_ids: action.target_question_ids,
              target_question_count: RuleEngine.targetCount(action, question, value, results)
            }
            : {
              action: 'duplicate_question',
              target_question_ids: action.target_question_ids,
              target_question_count: 1
            })
          break
        }
        case 'set_question_value': {
          if (checkOk) {
            actions.push(
              {
                action: 'set_question_value',
                target_question_ids: action.target_question_ids,
                value: action.value
              }
            )
          }
          break
        }
        case 'create_task':
          if (checkOk) {
            actions.push({
              action: 'create_task',
              task_data: action.task_data
            })
          }
          break
        case 'send_email':
          if (checkOk) {
            toast('result.mailOnSync')
          }
      }
      console.log(`Resulting actions:`, actions);
    }

    return actions
  }

  private static targetCount(action: IRuleAction, question: IQuestion, value: string, results: IResult[]): number {
    if (action.duplicate_mode !== 'question') return action.target_area_count
    if (action.duplicate_question_id === question.id) return parseInt(value) || 0

    const result = results.find((x) => (x.question?.id ?? x.question_id) === action.duplicate_question_id)
    return result ? parseInt(result.value) || 0 : 0
  }

  private static compareRules(question: IQuestion, ruleSet: IRuleSet, value: string): boolean {
    let checkOk = false

    let link_operator = 'or_operator'
    ruleSet.rules.forEach((rule) => {
    // if we have a numeric question we have to normalize and cast to float the value
    // to compare it as numeric
      const isNumeric = question.question_type === 'number' || question.question_type === 'rating'
      const numeric_value = isNumeric && value ? parseRuleNumber(value) : null
      const expected_numeric_value = isNumeric ? parseRuleNumber(rule.expected) : null
      const numericOk = numeric_value !== null && expected_numeric_value !== null

      // if no value is present then the condition is rated as false
      // no answer: no condition holds (offline; the backend treats "" as an answer)
      if (!value) checkOk = false
      else if (rule.operator === 'equals') {
        checkOk = RuleEngine.joinValues(checkOk, RuleEngine.checkEquals(value, rule.expected, question), link_operator)
      } else if (rule.operator === 'not_equals') {
        checkOk = RuleEngine.joinValues(
          checkOk,
          !RuleEngine.checkEquals(value, rule.expected, question),
          link_operator
        )
      } else if (rule.operator === 'greater_than') {
        checkOk = RuleEngine.joinValues(
          checkOk,
          numericOk && numeric_value > expected_numeric_value,
          link_operator
        )
      } else if (rule.operator === 'greater_or_equal_than') {
        checkOk = RuleEngine.joinValues(
          checkOk,
          numericOk && numeric_value >= expected_numeric_value,
          link_operator
        )
      } else if (rule.operator === 'less_than') {
        checkOk = RuleEngine.joinValues(
          checkOk,
          numericOk && numeric_value < expected_numeric_value,
          link_operator
        )
      } else if (rule.operator === 'less_or_equal_than') {
        checkOk = RuleEngine.joinValues(
          checkOk,
          numericOk && numeric_value <= expected_numeric_value,
          link_operator
        )
      } else if (rule.operator === 'contains') {
        checkOk = RuleEngine.joinValues(checkOk, value && value.includes(rule.expected), link_operator)
      } else if (rule.operator === 'starts_with') {
        checkOk = RuleEngine.joinValues(checkOk, value && value.startsWith(rule.expected), link_operator)
      } else if (rule.operator === 'ends_with') {
        checkOk = RuleEngine.joinValues(checkOk, value && value.endsWith(rule.expected), link_operator)
      } else if (rule.operator === 'deviates') {
        checkOk = RuleEngine.joinValues(checkOk, isExpectedDeviation(question, value), link_operator)
      }
      link_operator = rule.link_operator
    })
    return checkOk
  }

  private static joinValues(
    previous_check_result: boolean,
    current_check_result: boolean,
    link_operator: string
  ): boolean {
    if (link_operator === 'and_operator') return RuleEngine.andValue(previous_check_result, current_check_result)
    return RuleEngine.orValue(previous_check_result, current_check_result)
  }

  private static orValue(previous_check_result: boolean, current_check_result: boolean) {
    return previous_check_result || current_check_result
  }

  private static andValue(previous_check_result: boolean, current_check_result: boolean) {
    return previous_check_result && current_check_result
  }

  private static checkEquals(value: string, expected: string, question: IQuestion): boolean {
    if (question.question_type === 'dropdown' && question.is_multi) {
      if (!value) return false

      return equalsIgnoreOrder(value.split(','), expected.split(','))
    }

    // eslint-disable-next-line eqeqeq
    return value == expected
  }
}
